import React from "react";
import { clsx } from "clsx";
import typographyStyles from "@bloomberg/media-ui-tokens/css/typography.module.css";

import { ConditionalWrapper } from "~components/shared/conditionalWrapper";
import { type MediaConfig, Media, MediaFormat } from "~components/shared/media";
import { VariantBaseWithHeader } from "~components/shared/variant-base/VariantBase";
import { type VariationComponent, ZoneColumnCombo } from "~types/types";
import { filterForStoriesWithAudioImage } from "~utils/filterForStoriesWithAudioImage";
import { integrityError } from "~utils/moduleIntegrity";

import integrityCheck from "./LineupContentAudio6Up.integrity";
import { PREVIEW_NOTES } from "./previewNotes";
import styles from "./LineupContentAudio6Up.module.css";

const SUPPORTED_REGIONS: ZoneColumnCombo[] = [ZoneColumnCombo.leftContentWell];

export const MEDIA_PRIORITY: MediaFormat[] = [MediaFormat.audioImage];

const MEDIA_CONFIG: MediaConfig = {
    aspectRatio: 1,
    desktopMaxWidth: 600,
    showCaption: false,
    useImageLink: false,
};

const MAX_ITEMS = 6;

export default function LineupContentAudio6Up(props: VariationComponent) {
    const { alternateFirstItems: items = [] } = props;
    const storiesWithAudio = items ? filterForStoriesWithAudioImage(items) : [];
    const integrity = integrityCheck({ ...props });

    if (integrity.length) {
        return integrityError(integrity);
    }

    if (!storiesWithAudio?.length) {
        return null;
    }

    return (
        <VariantBaseWithHeader
            className={styles.LineupContentAudio6Up}
            gridClassName={styles.grid}
            previewNotes={PREVIEW_NOTES}
            source={props}
            supportedRegions={SUPPORTED_REGIONS}
        >
            <div className={styles.items} data-testid="audio6UpModuleItems">
                {storiesWithAudio.slice(0, MAX_ITEMS).map((item, index) => (
                    <div
                        className={styles.item}
                        key={item.id}
                        data-component="audio-featured-podcasts-card"
                    >
                        <Media
                            config={MEDIA_CONFIG}
                            priority={MEDIA_PRIORITY}
                            source={item}
                        />
                        <ConditionalWrapper
                            condition={Boolean(item.moduleMeta?.labelUrl)}
                            wrapper={(pageContent) => (
                                <a
                                    href={item.moduleMeta?.labelUrl}
                                    className={styles.cardLink}
                                    data-link-index={index + 1}
                                >
                                    {pageContent}
                                </a>
                            )}
                        >
                            <div className={styles.textContent}>
                                <h3
                                    className={clsx(
                                        styles.textContentTitle,
                                        typographyStyles.articleHeadlineXs
                                    )}
                                >
                                    {item.moduleMeta?.title}
                                </h3>

                                <p
                                    className={clsx(
                                        styles.textContentDescription,
                                        typographyStyles.coreShortTextSm
                                    )}
                                >
                                    {item.moduleMeta?.subtitle}
                                </p>
                            </div>
                        </ConditionalWrapper>
                    </div>
                ))}
            </div>
        </VariantBaseWithHeader>
    );
}
